import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils";
import {
    type QueryClient,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { IFilter, Page } from "../api.types";
import { IClause } from "../clauses";
import { invalidateOrganizationGroupQueries } from "../organization-groups/organization-groups";
import {
    IClauseChecklist,
    IClauseChecklistClausesCreate,
    IClauseChecklistCreate,
    IClauseChecklistDuplicate,
    IClauseChecklistImportPreprocessResponse,
    IClauseChecklistImportRequest,
    IClauseChecklistList,
    IClauseChecklistOrganization,
    IClauseChecklistOrganizationsCreate,
    IClauseChecklistRelevanceStatus,
    IClauseChecklistTopic,
    IClauseChecklistTopicClausesUpdate,
    IClauseChecklistTopicCreate,
    IClauseChecklistUpdate,
    ITopicWithEnabledClauses,
} from "./clause-checklists.types";
import { ITopicDetail, ITopicList } from "../topics";

const API = `${API_URL}/clause-checklists`;

export {
    clauseChecklistQueryKeys,
    invalidateClauseChecklistQueries,
} from "./clause-checklist-query-keys";

type ClauseChecklistWriteOperation = "create" | "update" | "delete";

export class ClauseChecklistRefreshUnconfirmedError extends Error {
    constructor(
        readonly operation: ClauseChecklistWriteOperation,
        readonly confirmedChecklistId: number,
        readonly confirmedChecklist: IClauseChecklist | null,
        readonly cause: unknown
    ) {
        const action =
            operation === "create"
                ? "created"
                : operation === "update"
                  ? "updated"
                  : "deleted";
        super(
            `The checklist was ${action}, but the view could not be refreshed. Reload to confirm.`
        );
        this.name = "ClauseChecklistRefreshUnconfirmedError";
    }
}

const refreshAfterChecklistWrite = async (
    queryClient: ReturnType<typeof useQueryClient>,
    operation: ClauseChecklistWriteOperation,
    confirmedChecklistId: number,
    confirmedChecklist: IClauseChecklist | null
) => {
    try {
        await invalidateOrganizationGroupQueries(queryClient, {
            throwOnError: true,
        });
    } catch (cause) {
        throw new ClauseChecklistRefreshUnconfirmedError(
            operation,
            confirmedChecklistId,
            confirmedChecklist,
            cause
        );
    }
};

export const createClauseChecklist = async (
    clauseChecklistCreate: IClauseChecklistCreate
) => {
    const result = await axios.post<IClauseChecklist>(
        API,
        clauseChecklistCreate
    );
    return result.data;
};

export const useCreateClauseChecklist = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createClauseChecklist,
        onSuccess: (checklist) =>
            refreshAfterChecklistWrite(
                queryClient,
                "create",
                checklist.id,
                checklist
            ),
    });
};

export const readClauseChecklists = async (
    page: number = 1,
    size: number = 50,
    filter?: IFilter,
    search?: string | null
) => {
    const url = buildUrl(API, { page, size, search, ...filter });

    const result = await axios.get<Page<IClauseChecklistList>>(url);
    return result.data;
};

export const readClauseChecklist = async (id: number) => {
    const result = await axios.get<IClauseChecklist>(`${API}/${id}`);
    return result.data;
};

export const updateClauseChecklist = async (
    id: number,
    clauseChecklistUpdate: IClauseChecklistUpdate
) => {
    const result = await axios.put<IClauseChecklist>(
        `${API}/${id}`,
        clauseChecklistUpdate
    );
    return result.data;
};

export const useUpdateClauseChecklist = (id: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (update: IClauseChecklistUpdate) =>
            updateClauseChecklist(id, update),
        onSuccess: (checklist) =>
            refreshAfterChecklistWrite(
                queryClient,
                "update",
                checklist.id,
                checklist
            ),
    });
};

export const deleteClauseChecklist = async (id: number) => {
    await axios.delete(`${API}/${id}`);
};

export interface ClauseChecklistBulkDeleteOutcome {
    deletedIds: number[];
    failedIds: number[];
    refreshUnconfirmed: ClauseChecklistRefreshUnconfirmedError | null;
}

export const deleteClauseChecklistsBulk = async (
    ids: readonly number[],
    queryClient: QueryClient
): Promise<ClauseChecklistBulkDeleteOutcome> => {
    const results = await Promise.allSettled(ids.map(deleteClauseChecklist));
    const deletedIds: number[] = [];
    const failedIds: number[] = [];

    results.forEach((result, index) => {
        const id = ids[index];
        if (id === undefined) return;
        if (result.status === "fulfilled") {
            deletedIds.push(id);
        } else {
            failedIds.push(id);
        }
    });

    let refreshUnconfirmed: ClauseChecklistRefreshUnconfirmedError | null =
        null;
    if (deletedIds.length > 0) {
        try {
            await invalidateOrganizationGroupQueries(queryClient, {
                throwOnError: true,
            });
        } catch (cause) {
            refreshUnconfirmed = new ClauseChecklistRefreshUnconfirmedError(
                "delete",
                deletedIds[0],
                null,
                cause
            );
        }
    }

    return { deletedIds, failedIds, refreshUnconfirmed };
};

export const useDeleteClauseChecklist = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteClauseChecklist,
        onSuccess: (_, id) =>
            refreshAfterChecklistWrite(queryClient, "delete", id, null),
    });
};

export const duplicateClauseChecklist = async (
    id: number,
    duplicate: IClauseChecklistDuplicate
) => {
    const result = await axios.post<IClauseChecklist>(
        `${API}/${id}/duplicate`,
        duplicate
    );
    return result.data;
};

export const addOrganizationsToClauseChecklist = async (
    id: number,
    clauseChecklistOrganizationsCreate: IClauseChecklistOrganizationsCreate
) => {
    const result = await axios.post<IClauseChecklistOrganization[]>(
        `${API}/${id}/organizations`,
        clauseChecklistOrganizationsCreate
    );

    return result.data;
};

export const removeOrganizationsFromClauseChecklist = async (
    id: number,
    organization_ids: number[]
) => {
    const params = organization_ids.map((id) => `org_ids=${id}`).join("&");

    const result = await axios.delete(`${API}/${id}/organizations?${params}`);
    return result.data;
};

export const addClausesToClauseChecklist = async (
    id: number,
    clauseChecklistClausesCreate: IClauseChecklistClausesCreate
) => {
    const result = await axios.post(
        `${API}/${id}/clauses`,
        clauseChecklistClausesCreate
    );

    return result.data;
};

export const removeClausesFromClauseChecklist = async (
    id: number,
    clause_ids: number[]
) => {
    const params = clause_ids.map((id) => `clause_ids=${id}`).join("&");

    const result = await axios.delete(`${API}/${id}/clauses?${params}`);
    return result.data;
};

export const getClausesForClauseChecklist = async (
    id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const url = buildUrl(`${API}/${id}/clauses`, { page, size, ...filter });
    const result = await axios.get<Page<IClause>>(url);
    return result.data;
};

export const getClauseChecklistScopeCount = async (id: number) => {
    const result = await axios.get<{ scope_count: number }>(
        `${API}/${id}/scopes`
    );
    return result.data;
};

export const exportClauseChecklist = async (id: number) => {
    const result = await axios.get(`${API}/${id}/export`, {
        responseType: "blob",
    });
    return result.data;
};

export const preprocessClauseChecklistImport = async (
    importIn: IClauseChecklistImportRequest
) => {
    const result = await axios.post<IClauseChecklistImportPreprocessResponse>(
        `${API}/import/preprocess`,
        importIn
    );
    return result.data;
};

export const importClauseChecklist = async (
    importIn: IClauseChecklistImportRequest
) => {
    const result = await axios.post<IClauseChecklist>(
        `${API}/import`,
        importIn
    );
    return result.data;
};

// --- Topic Management ---

export const addTopicToChecklist = async (
    id: number,
    topicCreate: IClauseChecklistTopicCreate
) => {
    const result = await axios.post<IClauseChecklistTopic>(
        `${API}/${id}/topics`,
        topicCreate
    );
    return result.data;
};

export const removeTopicFromChecklist = async (id: number, topicId: number) => {
    const result = await axios.delete(`${API}/${id}/topics/${topicId}`);
    return result.data;
};

export const getTopicsForChecklist = async (id: number) => {
    const result = await axios.get<ITopicList[]>(`${API}/${id}/topics`);
    return result.data;
};

// --- Clause Management for Topics ---

export const getTopicsWithClausesForChecklist = async (id: number) => {
    const result = await axios.get<ITopicWithEnabledClauses[]>(
        `${API}/${id}/topics/with-clauses`
    );
    return result.data;
};

export const updateEnabledClausesForTopics = async (
    id: number,
    updates: IClauseChecklistTopicClausesUpdate
) => {
    await axios.put(`${API}/${id}/topics/clauses`, updates);
};

export const addTopicToChecklistWithClauses = async (
    id: number,
    topicId: number,
    enabledClauses?: {
        topic_clause_id: number;
        document_section_type_id: number | null;
    }[]
) => {
    const body: IClauseChecklistTopicCreate = {
        topic_id: topicId,
        enabled_clauses: enabledClauses,
    };
    const result = await axios.post<IClauseChecklistTopic>(
        `${API}/${id}/topics`,
        body
    );
    return result.data;
};

export const getTopicForChecklist = async (
    checklistId: number,
    topicId: number
) => {
    const result = await axios.get<ITopicDetail>(
        `${API}/${checklistId}/topics/${topicId}`
    );
    return result.data;
};

export const getClauseChecklistRelevanceStatus = async (id: number) => {
    const result = await axios.get<IClauseChecklistRelevanceStatus>(
        `${API}/${id}/relevance-status`
    );
    return result.data;
};
